/**
 * Kontaktnetzwerk & Institutionen
 * Issues #19 (Scout-Button), #20 (BRIBE-Button),
 *        #21 (Edikt-Anzeige), #22 (Wahlkampf-Interface)
 */
import { state } from '../main';
import { fixture } from '../data/fixture';
import { t } from '../i18n';
import { scoutContact, bribeContact, supportCampaign, chainReferral, poachBid, counterBid } from '../api';

function loyBar(loy: number) {
  const c = loy > 60 ? 'var(--green)' : loy > 40 ? 'var(--amber)' : 'var(--red)';
  return `<div style="display:flex; align-items:center; gap:6px">
    <div style="width:60px; height:4px; background:var(--border); border-radius:2px; overflow:hidden">
      <div style="height:100%; width:${loy}%; background:${c}; border-radius:2px"></div>
    </div>
    <span style="font-family:var(--mono); font-size:11px; color:${c}">${loy}</span>
  </div>`;
}

// ── #19: Check if a suitable runner is available for scouting (INT >= 40)
function hasScoutRunner(): boolean {
  const runners = fixture.player.runners ?? [];
  return runners.some(r => r.status === 'active' && r.int >= 40);
}

// ── #21: Edikt badge color by type
function edictColor(type: string): { bg: string; border: string; text: string } {
  switch (type) {
    case 'TAX_HIKE':         return { bg: 'rgba(230,57,70,0.12)', border: 'rgba(230,57,70,0.4)', text: 'var(--red)' };
    case 'PATROL_INCREASE':  return { bg: 'rgba(252,163,17,0.12)', border: 'rgba(252,163,17,0.4)', text: 'var(--amber)' };
    case 'SUBSIDIES':        return { bg: 'rgba(6,214,160,0.12)', border: 'rgba(6,214,160,0.4)', text: 'var(--green)' };
    default:                 return { bg: 'rgba(255,255,255,0.05)', border: 'var(--border)', text: 'var(--text-mid)' };
  }
}

export function renderContacts(): HTMLElement {
  const institutions = fixture.player.institutions ?? [];
  const contacts     = fixture.player.contacts ?? [];

  const root = document.createElement('div');
  root.className = 'screen';

  const style = document.createElement('style');
  style.textContent = `
    .inst-grid { display:grid; grid-template-columns:repeat(4, 1fr); gap:10px; margin-bottom:16px; }
    .inst-card { background:var(--surface-frost); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); border:1px solid var(--line); border-radius:4px; padding:12px; }
    .inst-card:hover { border-color:var(--border-hi); }
    .inst-tier { font-family:var(--mono); font-size:11px; letter-spacing:1px; margin-bottom:4px; }
    .inst-name { font-size:13px; font-weight:600; color:var(--text-hi); margin-bottom:6px; }
    .inst-progress { height:3px; background:var(--border); border-radius:2px; overflow:hidden; margin-top:6px; }
    .inst-progress-fill { height:100%; background:var(--cyan); border-radius:2px; }
    /* .contact-table → shared .dtable (marker kept for tests) */
    .c3-dot { display:inline-block; width:8px; height:8px; border-radius:50%; margin-right:2px; }
    /* .scout-btn → shared .btn.sm · .bribe-btn → shared .btn.sm.amber (markers kept for JS+tests) */
    .edict-badge { display:inline-flex; align-items:center; gap:6px; padding:5px 10px; border-radius:3px; font-size:11px; font-family:var(--mono); margin-bottom:4px; }
    .election-panel { margin-bottom:16px; }
    .election-bar { height:6px; background:var(--border); border-radius:3px; overflow:hidden; }
    .election-bar-fill { height:100%; border-radius:3px; }
    .candidate-row { display:flex; align-items:center; gap:10px; padding:5px 0; border-bottom:1px solid var(--border); font-size:12px; }
    .candidate-row:last-child { border-bottom:none; }
    @media (max-width:1024px) {
      .inst-grid { grid-template-columns:repeat(auto-fill, minmax(200px, 1fr)); }
    }
    /* M4a Contact Drama Arcs */
    /* .chain-btn → shared .btn.sm.violet · .counter-btn → shared .btn.sm (markers kept for JS) */
    .poach-banner-row td { padding:0; background:rgba(230,57,70,0.08); border-left:3px solid var(--red); }
    .poach-banner { display:flex; align-items:center; gap:10px; padding:8px 12px; font-size:12px; }
    .poach-icon { color:var(--red); font-size:14px; font-weight:bold; }
    .poach-text { color:var(--text-hi); }
    .poach-pending-note { color:var(--text-lo); font-family:var(--mono); font-size:11px; margin-left:auto; }
    .poach-form { margin-top:16px; padding:12px; background:var(--surface-frost); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); border:1px solid var(--line); border-radius:4px; }
    .poach-form-title { font-family:var(--mono); font-size:12px; letter-spacing:1px; text-transform:uppercase; color:var(--red); margin-bottom:8px; }
    .poach-form-row { display:flex; gap:8px; align-items:center; margin-bottom:8px; font-size:12px; }
    .poach-form-row label { min-width:120px; color:var(--text-lo); font-family:var(--mono); font-size:11px; }
    .poach-form-row input, .poach-form-row select { flex:1; background:var(--bg); border:1px solid var(--border); color:var(--text-hi); padding:5px 8px; font-family:var(--mono); font-size:12px; border-radius:3px; }
    .poach-form-row input:focus, .poach-form-row select:focus { border-color:var(--cyan); outline:none; }
    /* .poach-submit-btn → shared .btn.sm.red (marker kept) */
    .poach-nap-note { font-size:11px; color:var(--text-lo); font-style:italic; margin-top:6px; }
  `;
  root.appendChild(style);

  const tierColors: Record<number, string> = { 1: 'var(--green)', 2: 'var(--amber)', 3: 'var(--red)' };
  const tierLabel: Record<number, string>  = { 1: 'TIER 1', 2: 'TIER 2', 3: 'TIER 3' };
  const canScout = hasScoutRunner();

  // ── Institution cards (with #19 Scout-Button + #21 Edikt-Anzeige) ──
  const instCardsHtml = institutions.map(inst => {
    const c   = tierColors[inst.tier] ?? 'var(--text-lo)';
    const pct = inst.maxContacts > 0 ? Math.round(inst.contacts / inst.maxContacts * 100) : 0;

    // #19: Scout button when no contacts
    let scoutHtml = '';
    if (inst.contacts === 0) {
      scoutHtml = canScout
        ? `<button class="scout-btn btn sm" data-scout-inst="${inst.id}">${t('contacts.btn_scout')}</button>`
        : `<button class="scout-btn btn sm" disabled title="${t('contacts.btn_scout_disabled_title')}">${t('contacts.btn_scout')}</button>`;
    }

    // #21: Edikt badges for district_administration
    let ediktHtml = '';
    const edicts = inst.activeEdicts ?? [];
    if (inst.type === 'district_administration' && edicts.length > 0) {
      ediktHtml = `<div class="inst-edicts" style="margin-top:8px">
        <div style="font-size:11px;color:var(--text-lo);text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">${t('contacts.active_edicts')}</div>
        ${edicts.map(e => {
          const ec = edictColor(e.type);
          return `<div class="edict-badge" style="background:${ec.bg};border:1px solid ${ec.border};color:${ec.text}">
            <span style="font-weight:600">${e.type.replace(/_/g, ' ')}</span>
            <span style="opacity:0.7">${t('contacts.edict_until', { day: String(e.expiresDay) })}</span>
          </div>`;
        }).join('')}
      </div>`;
    }

    return `<div class="inst-card" data-inst-type="${inst.type ?? ''}">
      <div class="inst-tier" style="color:${c}">${tierLabel[inst.tier] ?? `TIER ${inst.tier}`}</div>
      <div class="inst-name">${inst.name}</div>
      <div style="font-size:12px; color:var(--text-lo)">${t('contacts.inst_contacts')}<span style="color:${c}">${inst.contacts}</span> / ${inst.maxContacts}</div>
      <div class="inst-progress"><div class="inst-progress-fill" style="width:${pct}%; background:${c}"></div></div>
      ${scoutHtml}
      ${ediktHtml}
    </div>`;
  }).join('');

  // ── Contact rows (with #20 BRIBE-Button + M4a CHAIN / POACH-incoming) ──
  const contactRowsHtml = contacts.map(c => {
    const inst = institutions.find(i => i.id === c.inst);
    const c3Dots = Array(3).fill(0).map((_, i) =>
      `<span class="c3-dot" style="background:${i < c.c3 ? 'var(--cyan)' : 'var(--border)'}"></span>`
    ).join('');

    // #20: BRIBE button for discovered contacts
    let actionBtn: string;
    const status = c.status ?? (c.state === 'payroll' ? 'active' : c.state === 'discovered' ? 'discovered' : 'active');
    if (status === 'discovered') {
      const cost = c.bribeCost ?? 0;
      const canAfford = (fixture.player.cleanCash ?? 0) >= cost;
      actionBtn = canAfford
        ? `<button class="bribe-btn btn sm amber" data-bribe-id="${c.id ?? c.name}">${t('contacts.btn_bribe', { cost: cost.toLocaleString('de-DE') })}</button>`
        : `<button class="bribe-btn btn sm amber" disabled title="${t('contacts.btn_bribe_disabled_title')}">${t('contacts.btn_bribe', { cost: cost.toLocaleString('de-DE') })}</button>`;
    } else {
      actionBtn = `<button style="background:rgba(var(--ac),0.07); border:1px solid var(--border); color:var(--cyan-dim); font-size:11px; padding:3px 10px; border-radius:3px; cursor:pointer">${t('contacts.btn_bribery')}</button>`;
    }

    // M4a: CHAIN_REFERRAL button — qualifies only for payroll contacts with
    // daysOnPayroll≥30, chainUsed=false, loyalty≥20.
    let chainBtn = '';
    const isPayroll = c.state === 'payroll';
    const daysOnPayroll = c.daysOnPayroll ?? 0;
    const chainUsed = c.chainUsed ?? false;
    if (isPayroll && daysOnPayroll >= 30 && !chainUsed && c.loy >= 20) {
      const chancePct = c.loy;
      chainBtn = `<button class="chain-btn btn sm violet" data-chain-id="${c.id ?? c.name}" title="${t('contacts.chain_tooltip', { chance: String(chancePct) })}">${t('contacts.btn_chain', { chance: String(chancePct) })}</button>`;
    } else if (isPayroll && !chainUsed) {
      // Show disabled gate tooltip so players learn the requirement.
      const remaining = Math.max(0, 30 - daysOnPayroll);
      chainBtn = `<button class="chain-btn btn sm violet" disabled title="${t('contacts.chain_locked', { days: String(remaining) })}">${t('contacts.btn_chain_locked')}</button>`;
    }

    // M4a: PendingPoach indicator — victim sees anonymous bid + CounterBid option.
    let poachBadgeRow = '';
    if (c.pendingPoach && (c.status !== 'discovered')) {
      const bid = c.pendingPoach.amount;
      const already = (c.pendingPoach.counterBid ?? 0) > 0;
      const counterBtn = already
        ? `<span class="poach-pending-note">${t('contacts.poach_counter_submitted', { amount: (c.pendingPoach.counterBid ?? 0).toLocaleString('de-DE') })}</span>`
        : `<button class="counter-btn btn sm" style="margin-left:auto" data-counter-id="${c.id ?? c.name}" data-bid="${bid}">${t('contacts.btn_counter')}</button>`;
      poachBadgeRow = `<tr class="poach-banner-row" data-poach-for="${c.id ?? c.name}">
        <td colspan="7" class="poach-banner">
          <span class="poach-icon">⚠</span>
          <span class="poach-text">${t('contacts.poach_incoming', { amount: bid.toLocaleString('de-DE') })}</span>
          ${counterBtn}
        </td>
      </tr>`;
    }

    // Status badge
    const statusBadge = status === 'discovered'
      ? `<span style="font-family:var(--mono);font-size:11px;padding:1px 6px;border-radius:2px;background:rgba(252,163,17,0.12);border:1px solid rgba(252,163,17,0.3);color:var(--amber)">DISCOVERED</span>`
      : status === 'active'
      ? `<span style="font-family:var(--mono);font-size:11px;padding:1px 6px;border-radius:2px;background:rgba(var(--ac),0.08);border:1px solid rgba(var(--ac),0.2);color:var(--cyan-dim)">ACTIVE</span>`
      : '';

    return `<tr data-contact-status="${status}" data-contact-id="${c.id ?? c.name}">
      <td style="color:var(--text-hi); font-weight:600">${c.name} ${statusBadge}</td>
      <td style="color:var(--text-mid)">${inst?.name ?? c.inst}</td>
      <td>${loyBar(c.loy)}</td>
      <td title="C3 Verfall (${c.c3}/3)">${c3Dots}</td>
      <td style="font-family:var(--mono); font-size:11px; color:${c.cooldown > 0 ? 'var(--amber)' : 'var(--green)'}">
        ${c.cooldown > 0 ? t('contacts.cooldown_days', { days: String(c.cooldown) }) : t('contacts.cooldown_ready')}
      </td>
      <td style="font-family:var(--mono); font-size:11px; color:var(--text-mid)">$${c.cost.toLocaleString('de-DE')}${t('contacts.cost_per_month')}</td>
      <td style="display:flex;gap:4px;flex-wrap:wrap">${actionBtn}${chainBtn}</td>
    </tr>${poachBadgeRow}`;
  }).join('');

  // ── #22: Election panel ──
  const election = fixture.election;
  const showElection = election && election.cyclesUntilElection <= 5;
  let electionHtml = '';
  if (showElection && election) {
    const top3 = (election.candidates ?? []).slice(0, 3);
    const candidateColors = ['var(--cyan)', 'var(--amber)', 'var(--red)'];
    electionHtml = `
    <div class="panel election-panel" id="election-panel">
      <div class="panel-title">${t('contacts.election_title')}</div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
        <span style="font-family:var(--mono);font-size:13px;color:var(--text-hi)">
          ${t('contacts.election_cycles', { cycles: String(election.cyclesUntilElection) })}
        </span>
      </div>
      <div style="margin-bottom:12px">
        <div style="font-size:12px;color:var(--text-lo);text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">${t('contacts.vote_power')}</div>
        <div style="display:flex;align-items:center;gap:8px">
          <div class="election-bar" style="flex:1">
            <div class="election-bar-fill" style="width:${election.votePower}%;background:var(--cyan)"></div>
          </div>
          <span style="font-family:var(--mono);font-size:12px;color:var(--cyan)">${election.votePower}/100</span>
        </div>
      </div>
      <div style="font-size:12px;color:var(--text-lo);text-transform:uppercase;letter-spacing:1px;margin-bottom:6px">${t('contacts.top_candidates')}</div>
      ${top3.map((cand, i) => `
        <div class="candidate-row">
          <span style="width:20px;text-align:center;color:${candidateColors[i] ?? 'var(--text-mid)'};font-weight:bold;font-family:var(--mono)">#${i + 1}</span>
          <span style="flex:1;color:var(--text-hi)">${cand.name}</span>
          <span style="font-family:var(--mono);font-size:11px;color:var(--text-lo)">${cand.party}</span>
          <span style="font-family:var(--mono);font-size:11px;color:${candidateColors[i] ?? 'var(--text-mid)'}">${cand.supportPercent}%</span>
        </div>
      `).join('')}
      <button class="scout-btn btn sm" id="btn-support-campaign" style="margin-top:10px;width:100%;text-align:center">${t('contacts.btn_support')}</button>
    </div>`;
  }

  // ── Federal Heat ──
  const fh = state.federalHeat;
  const heatLevel = fh >= 90 ? 'INDICTMENT' : fh >= 75 ? 'RED' : fh >= 60 ? 'ORANGE' : fh >= 40 ? 'YELLOW' : 'GREEN';
  const heatColor = fh >= 75 ? 'var(--red)' : fh >= 60 ? 'var(--orange)' : fh >= 40 ? 'var(--amber)' : 'var(--green)';

  root.innerHTML += `
    <div class="screen-title">${t('contacts.title')}</div>

    ${electionHtml}

    <div class="panel" style="margin-bottom:16px">
      <div class="panel-title">${t('contacts.panel_fed_heat')}</div>
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px">
        <span style="font-family:var(--mono); font-size:22px; color:${heatColor}; font-weight:bold">${fh}<span style="font-size:13px; color:var(--text-lo)">/100</span></span>
        <span id="fh-level-badge" style="font-family:var(--mono); font-size:11px; padding:3px 12px; border-radius:3px; background:${heatColor}22; border:1px solid ${heatColor}55; color:${heatColor}">${heatLevel}</span>
      </div>
      <div style="position:relative; height:8px; display:flex; border-radius:4px; overflow:hidden; margin-bottom:4px">
        <div style="flex:40; background:var(--green); opacity:0.3"></div>
        <div style="flex:20; background:var(--amber); opacity:0.3"></div>
        <div style="flex:15; background:var(--orange); opacity:0.3"></div>
        <div style="flex:15; background:var(--red); opacity:0.3"></div>
        <div style="flex:10; background:var(--magenta); opacity:0.3"></div>
      </div>
      <div style="position:relative; height:4px; background:transparent; margin-bottom:8px">
        <div style="position:absolute; left:${fh}%; transform:translateX(-50%)">
          <div style="width:2px; height:10px; background:${heatColor}; margin-top:-8px"></div>
        </div>
      </div>
      <div style="display:flex; gap:4px; font-family:var(--mono); font-size:11px; letter-spacing:0.5px; margin-bottom:8px">
        <span style="flex:40; color:var(--green)">GREEN (0–39)</span>
        <span style="flex:20; color:var(--amber)">YELLOW (40–59)</span>
        <span style="flex:15; color:var(--orange)">ORANGE (60–74)</span>
        <span style="flex:15; color:var(--red)">RED (75–89)</span>
        <span style="flex:10; color:var(--magenta)">INDICT</span>
      </div>
      <div style="font-size:12px; color:var(--text-lo); font-family:var(--mono)">
        ${t('contacts.daily_decay')} &nbsp;|&nbsp; Trend: <span style="color:${heatColor}">${t('contacts.trend_stagnant')}</span>
      </div>
    </div>

    <div class="inst-grid">${instCardsHtml}</div>

    <div class="panel">
      <div class="panel-title">${t('contacts.panel_contacts', { count: String(contacts.length) })}</div>
      <table class="contact-table dtable">
        <thead><tr>
          <th>${t('contacts.col_name')}</th><th>${t('contacts.col_institution')}</th><th>${t('contacts.col_loyalty')}</th><th>${t('contacts.col_c3')}</th><th>${t('contacts.col_cooldown')}</th><th>${t('contacts.col_cost')}</th><th><span class="sr-only">Aktionen</span></th>
        </tr></thead>
        <tbody>${contactRowsHtml}</tbody>
      </table>
      <button class="dash-link" style="margin-top:10px">${t('contacts.btn_new_contact')}</button>

      <!-- M4a POACH form: manual bid against a rival's contact. Advanced feature —
           requires knowledge of victim contact ID (from intel/gazette). No NAP break. -->
      <div class="poach-form">
        <div class="poach-form-title">⚔ ${t('contacts.poach_form_title')}</div>
        <div class="poach-form-row">
          <label for="poach-victim-idx">${t('contacts.poach_field_victim')}</label>
          <input type="number" id="poach-victim-idx" min="0" placeholder="0" />
        </div>
        <div class="poach-form-row">
          <label for="poach-contact-id">${t('contacts.poach_field_contact')}</label>
          <input type="text" id="poach-contact-id" placeholder="corpsec_0" />
        </div>
        <div class="poach-form-row">
          <label for="poach-amount">${t('contacts.poach_field_amount')}</label>
          <input type="number" id="poach-amount" min="0" step="100" placeholder="5000" />
        </div>
        <div style="display:flex; align-items:center; gap:10px">
          <button class="poach-submit-btn btn sm red" id="btn-poach-submit">${t('contacts.btn_poach_submit')}</button>
          <span class="poach-nap-note">${t('contacts.poach_nap_note')}</span>
        </div>
      </div>
    </div>
  `;

  // ── Event handlers ──

  // #19: Scout buttons
  root.querySelectorAll<HTMLElement>('.scout-btn[data-scout-inst]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const btnEl = btn as HTMLButtonElement;
      const instId = btnEl.dataset.scoutInst;
      if (!instId || btnEl.disabled) return;

      const oldText = btnEl.textContent;
      btnEl.textContent = '...';
      btnEl.disabled = true;
      try {
        const sid = state.sessionId;
        await scoutContact(sid, { contactId: instId });
        btnEl.textContent = t('contacts.btn_state_scouted');
      } catch (e: unknown) {
        btnEl.textContent = t('contacts.btn_state_failed');
        console.error('[contacts] Scout failed:', (e instanceof Error ? e.message : String(e)));
        setTimeout(() => {
          btnEl.textContent = oldText;
          btnEl.disabled = false;
        }, 2000);
      }
    });
  });

  // #20: Bribe buttons
  root.querySelectorAll<HTMLElement>('.bribe-btn[data-bribe-id]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const btnEl = btn as HTMLButtonElement;
      const bId = btnEl.dataset.bribeId;
      if (!bId || btnEl.disabled) return;

      const oldText = btnEl.textContent;
      btnEl.textContent = '...';
      btnEl.disabled = true;
      try {
        const sid = state.sessionId;
        const contact = contacts.find(c => (c.id ?? c.name) === bId);
        await bribeContact(sid, { contactId: bId, amount: contact?.bribeCost ?? 0 });
        btnEl.textContent = t('contacts.btn_state_bribed');
      } catch (e: unknown) {
        btnEl.textContent = t('contacts.btn_state_failed');
        console.error('[contacts] Bribe failed:', (e instanceof Error ? e.message : String(e)));
        setTimeout(() => {
          btnEl.textContent = oldText;
          btnEl.disabled = false;
        }, 2000);
      }
    });
  });

  // M4a: CHAIN_REFERRAL buttons
  root.querySelectorAll<HTMLElement>('.chain-btn[data-chain-id]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const btnEl = btn as HTMLButtonElement;
      const cId = btnEl.dataset.chainId;
      if (!cId || btnEl.disabled) return;

      const oldText = btnEl.textContent;
      btnEl.textContent = '...';
      btnEl.disabled = true;
      try {
        const sid = state.sessionId;
        const res = await chainReferral(sid, { contactId: cId });
        btnEl.textContent = res.status === 'failed'
          ? t('contacts.btn_state_chain_failed')
          : t('contacts.btn_state_chain_ok');
      } catch (e: unknown) {
        btnEl.textContent = t('contacts.btn_state_failed');
        console.error('[contacts] Chain referral failed:', (e instanceof Error ? e.message : String(e)));
        setTimeout(() => {
          btnEl.textContent = oldText;
          btnEl.disabled = false;
        }, 2000);
      }
    });
  });

  // M4a: COUNTER_BID buttons (on pending-poach rows)
  root.querySelectorAll<HTMLElement>('.counter-btn[data-counter-id]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const btnEl = btn as HTMLButtonElement;
      const cId = btnEl.dataset.counterId;
      const currentBid = Number(btnEl.dataset.bid ?? '0');
      if (!cId || btnEl.disabled) return;

      // Prompt for counter amount. MVP — a modal would be nicer; see follow-up.
      const suggestedMin = Math.ceil(currentBid * 1.1);
      const input = window.prompt(
        t('contacts.counter_prompt', { current: currentBid.toLocaleString('de-DE'), min: suggestedMin.toLocaleString('de-DE') }),
        String(suggestedMin)
      );
      if (!input) return;
      const amount = Number(input);
      if (!Number.isFinite(amount) || amount <= 0) return;

      const oldText = btnEl.textContent;
      btnEl.textContent = '...';
      btnEl.disabled = true;
      try {
        const sid = state.sessionId;
        await counterBid(sid, { contactId: cId, amount });
        btnEl.textContent = t('contacts.btn_state_counter_ok');
      } catch (e: unknown) {
        btnEl.textContent = t('contacts.btn_state_failed');
        console.error('[contacts] Counter-bid failed:', (e instanceof Error ? e.message : String(e)));
        setTimeout(() => {
          btnEl.textContent = oldText;
          btnEl.disabled = false;
        }, 2000);
      }
    });
  });

  // M4a: POACH form submit
  const poachBtn = root.querySelector<HTMLButtonElement>('#btn-poach-submit');
  if (poachBtn) {
    poachBtn.addEventListener('click', async () => {
      if (poachBtn.disabled) return;
      const victimInput = root.querySelector<HTMLInputElement>('#poach-victim-idx');
      const contactInput = root.querySelector<HTMLInputElement>('#poach-contact-id');
      const amountInput = root.querySelector<HTMLInputElement>('#poach-amount');
      const victimIdx = Number(victimInput?.value ?? '');
      const contactId = (contactInput?.value ?? '').trim();
      const amount = Number(amountInput?.value ?? '');
      if (!Number.isFinite(victimIdx) || victimIdx < 0 || !contactId || !Number.isFinite(amount) || amount <= 0) {
        poachBtn.textContent = t('contacts.poach_invalid_input');
        setTimeout(() => { poachBtn.textContent = t('contacts.btn_poach_submit'); }, 2000);
        return;
      }
      const oldText = poachBtn.textContent;
      poachBtn.textContent = '...';
      poachBtn.disabled = true;
      try {
        const sid = state.sessionId;
        await poachBid(sid, { victimPlayerIdx: victimIdx, contactId, amount });
        poachBtn.textContent = t('contacts.btn_state_poach_ok');
      } catch (e: unknown) {
        poachBtn.textContent = t('contacts.btn_state_failed');
        console.error('[contacts] Poach bid failed:', (e instanceof Error ? e.message : String(e)));
        setTimeout(() => {
          poachBtn.textContent = oldText;
          poachBtn.disabled = false;
        }, 2000);
      }
    });
  }

  // #22: Campaign support button
  const campaignBtn = root.querySelector('#btn-support-campaign');
  if (campaignBtn) {
    campaignBtn.addEventListener('click', async () => {
      const btnEl = campaignBtn as HTMLButtonElement;
      if (btnEl.disabled) return;
      const oldText = btnEl.textContent;
      btnEl.textContent = '...';
      btnEl.disabled = true;
      try {
        const sid = state.sessionId;
        const electionData = fixture.election;
        await supportCampaign(sid, { campaignId: electionData?.id ?? 'local_election' });
        btnEl.textContent = t('contacts.btn_state_supported');
      } catch (e: unknown) {
        btnEl.textContent = t('contacts.btn_state_failed');
        console.error('[contacts] Campaign support failed:', (e instanceof Error ? e.message : String(e)));
        setTimeout(() => {
          btnEl.textContent = oldText;
          btnEl.disabled = false;
        }, 2000);
      }
    });
  }

  return root;
}
